package link.erpango.quip.features.noteimport

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import link.erpango.quip.domain.entities.DSparePartCatalog
import link.erpango.quip.domain.noteimport.ImportDraft
import link.erpango.quip.domain.noteimport.MaintenanceDraft
import link.erpango.quip.domain.noteimport.NoteImportEvent
import link.erpango.quip.domain.noteimport.NoteImportTarget
import link.erpango.quip.domain.noteimport.SparePartDraft
import link.erpango.quip.domain.noteimport.VehicleDraft
import link.erpango.quip.domain.noteimport.VehicleRef
import link.erpango.quip.domain.noteimport.toDSparePart
import link.erpango.quip.domain.usecases.INoteImportUseCase

/** What the user chose in the dialog; the form fills its fields from it. */
sealed interface NoteImportSelection {
    /** The analyzed note, so the form can quote where each value came from. */
    val note: String

    data class Vehicle(val draft: VehicleDraft, override val note: String) : NoteImportSelection

    /** [parts] are complete enough to become maintenance spare parts. */
    data class Maintenance(
        val draft: MaintenanceDraft,
        val parts: List<SparePartDraft>,
        override val note: String,
    ) : NoteImportSelection

    /** [garageVin] is the garage vehicle the part was associated with, if any. */
    data class SparePart(
        val draft: SparePartDraft,
        val garageVin: String?,
        override val note: String,
    ) : NoteImportSelection
}

data class NoteImportUiState(
    val target: NoteImportTarget = NoteImportTarget.VEHICLE,
    /** Garage vehicle the form is about (maintenance form). */
    val contextVehicleVin: String? = null,
    val analysis: NoteAnalysisState = NoteAnalysisState(),
    val selectedVehicleId: String? = null,
    val selectedMaintenanceId: String? = null,
    val selectedPartIds: Set<String> = emptySet(),
) {
    val draft: ImportDraft? get() = analysis.draft

    /** Maintenances of the context vehicle first, then unassigned ones, then the rest. */
    val maintenanceCandidates: List<MaintenanceDraft>
        get() = draft?.maintenances.orEmpty().sortedBy { maintenance ->
            when (resolveGarageVin(maintenance.vehicle)) {
                contextVehicleVin -> 0
                null -> 1
                else -> 2
            }.takeIf { contextVehicleVin != null } ?: 0
        }

    val canApply: Boolean
        get() = draft != null && when (target) {
            NoteImportTarget.VEHICLE -> selectedVehicleId != null
            NoteImportTarget.MAINTENANCE -> selectedMaintenanceId != null || selectedPartIds.isNotEmpty()
            NoteImportTarget.SPARE_PART -> selectedPartIds.isNotEmpty()
            NoteImportTarget.BULK -> false
        }

    /** The garage VIN a draft reference points at: a garage vehicle or a draft matched to one. */
    fun resolveGarageVin(ref: VehicleRef): String? = when (ref) {
        is VehicleRef.Garage -> ref.vin
        is VehicleRef.Draft -> draft?.vehicle(ref.draftId)?.garageMatchVin
        VehicleRef.Unassigned -> null
    }
}

/**
 * State of the "Rellenar desde nota" dialog opened from a creation form. The analysis itself is
 * in [NoteAnalysisController]; this adds the candidate selection for one [NoteImportTarget].
 *
 * The factory keeps ViewModels for the whole session, so [open] resets everything each time
 * the dialog is shown.
 */
class NoteImportViewModel(
    private val useCase: INoteImportUseCase,
    private val sparePartCatalog: DSparePartCatalog,
) : ViewModel() {

    private val selection = MutableStateFlow(NoteImportUiState())

    val analysis = NoteAnalysisController(useCase, viewModelScope, onDraft = ::selectDefaults)

    val uiState: StateFlow<NoteImportUiState> = combine(selection, analysis.state) { sel, analysisState ->
        sel.copy(analysis = analysisState)
    }.stateIn(viewModelScope, SharingStarted.Eagerly, NoteImportUiState())

    fun open(target: NoteImportTarget, contextVehicleVin: String? = null) {
        selection.value = NoteImportUiState(target = target, contextVehicleVin = contextVehicleVin)
        analysis.start()
        useCase.track(NoteImportEvent.Opened(target))
    }

    fun selectVehicle(id: String) = selection.update { it.copy(selectedVehicleId = id) }

    fun selectMaintenance(id: String) = selection.update { it.copy(selectedMaintenanceId = id) }

    fun togglePart(id: String) = selection.update { state ->
        when {
            // The spare part form holds a single part: selecting one replaces the other.
            state.target == NoteImportTarget.SPARE_PART -> state.copy(selectedPartIds = setOf(id))
            id in state.selectedPartIds -> state.copy(selectedPartIds = state.selectedPartIds - id)
            else -> state.copy(selectedPartIds = state.selectedPartIds + id)
        }
    }

    /** True when a part has everything a maintenance spare part needs (type, brand, model, quantity). */
    fun isCompleteForMaintenance(part: SparePartDraft): Boolean = part.toDSparePart(sparePartCatalog) != null

    /** The chosen candidate, or null when nothing is selected. Records the "applied" event. */
    fun buildSelection(): NoteImportSelection? {
        val state = currentState()
        val draft = state.draft ?: return null
        val note = state.analysis.analyzedNote
        val result = when (state.target) {
            NoteImportTarget.VEHICLE ->
                draft.vehicles.firstOrNull { it.id == state.selectedVehicleId }
                    ?.let { NoteImportSelection.Vehicle(it, note) }

            NoteImportTarget.MAINTENANCE -> {
                val maintenance = draft.maintenances.firstOrNull { it.id == state.selectedMaintenanceId }
                val parts = draft.spareParts.filter { it.id in state.selectedPartIds && isCompleteForMaintenance(it) }
                if (maintenance == null && parts.isEmpty()) null
                else NoteImportSelection.Maintenance(maintenance ?: MaintenanceDraft(id = "none"), parts, note)
            }

            NoteImportTarget.SPARE_PART ->
                draft.spareParts.firstOrNull { it.id in state.selectedPartIds }
                    ?.let { NoteImportSelection.SparePart(it, state.resolveGarageVin(it.vehicle), note) }

            NoteImportTarget.BULK -> null
        }
        if (result != null) useCase.track(NoteImportEvent.Applied(state.target))
        return result
    }

    private fun currentState(): NoteImportUiState = selection.value.copy(analysis = analysis.state.value)

    /** Clears the note and the result (the dialog closed or its data was applied). */
    fun reset() {
        analysis.clear()
        selection.value = NoteImportUiState()
    }

    /** Preselects the most likely candidate after every analysis, so one tap is enough. */
    private fun selectDefaults(draft: ImportDraft) {
        selection.update { current ->
            val state = current.copy(analysis = current.analysis.copy(draft = draft))
            when (state.target) {
                NoteImportTarget.VEHICLE -> state.copy(
                    selectedVehicleId = draft.vehicles.firstOrNull { it.garageMatchVin == null }?.id
                        ?: draft.vehicles.firstOrNull()?.id,
                )

                NoteImportTarget.MAINTENANCE -> {
                    val maintenance = state.maintenanceCandidates.firstOrNull()
                    val parts = draft.spareParts.filter { part ->
                        isCompleteForMaintenance(part) &&
                            (maintenance == null || part.vehicle == maintenance.vehicle || part.vehicle == VehicleRef.Unassigned)
                    }
                    state.copy(selectedMaintenanceId = maintenance?.id, selectedPartIds = parts.map { it.id }.toSet())
                }

                NoteImportTarget.SPARE_PART -> state.copy(
                    selectedPartIds = draft.spareParts.firstOrNull()?.let { setOf(it.id) }.orEmpty(),
                )

                NoteImportTarget.BULK -> state
            }.copy(analysis = NoteAnalysisState())
        }
    }
}
